import { z } from 'zod';

// Add proper type declaration for Vite's import.meta.env
interface ImportMetaEnv {
  [key: string]: string | undefined;
  // Add specific environment variables you know exist
  NEXT_PUBLIC_APP_HOST: string;
  NEXT_PUBLIC_API_HOST?: string;
  NEXT_PUBLIC_WEBSOCKET_URL: string;
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: string;
  NEXT_PUBLIC_MAPBOXGL_ACCESS_TOKEN: string;
  NEXT_PUBLIC_QUICKBOOKS_ONLINE_URL: string;
  NEXT_PUBLIC_SUPABASE_ANON_KEY: string;
  NEXT_PUBLIC_SUPABASE_URL: string;
  NEXT_PUBLIC_TILLED_PUBLISHABLE_KEY: string;
  NEXT_PUBLIC_TILLED_SANDBOX_PUBLISHABLE_KEY: string;
  NEXT_PUBLIC_TRASHLAB_ENV: string;
  NEXT_PUBLIC_VAPID_PUBLIC_KEY: string;
  NEXT_PUBLIC_RUTTER_PUBLIC_KEY?: string;
  NEXT_PUBLIC_SENTRY_DSN?: string;
  NEXT_PUBLIC_RENDER_GIT_COMMIT: string;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}

declare global {
  interface Window {
    env?: Record<string, string>;
  }
}

if (typeof process === 'undefined') {
  (window as any).process = {
    env: {
      ...import.meta.env,
      NODE_ENV: 'development',
    },
  };
}

const envSchema = z.object({
  NEXT_PUBLIC_APP_HOST: z.string(),
  NEXT_PUBLIC_API_HOST: z.string().optional(),
  NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string(),
  NEXT_PUBLIC_SUPABASE_URL: z.string(),
  NEXT_PUBLIC_TRASHLAB_ENV: z.string(),
  NEXT_PUBLIC_VAPID_PUBLIC_KEY: z.string(),
  NEXT_PUBLIC_RENDER_GIT_COMMIT: z.string(),
  NEXT_PUBLIC_DEFAULT_TENANT_ID: z.string(),
  NEXT_PUBLIC_DEFAULT_DOMAIN: z.string(),
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: z.string().optional(),
  NEXT_PUBLIC_TILLED_PUBLISHABLE_KEY: z.string(),
  NEXT_PUBLIC_TILLED_SANDBOX_PUBLISHABLE_KEY: z.string(),
  NEXT_PUBLIC_MAPBOXGL_ACCESS_TOKEN: z.string().optional(),
  NEXT_PUBLIC_JABRA_KEY: z.string().optional(),
  NEXT_PUBLIC_WEBSOCKET_URL: z.string().optional(),
});

// Merge build-time env with runtime overrides so dynamically injected values
// can replace host-specific settings without dropping required baked values.
const envSource = {
  ...(typeof process !== 'undefined' ? process.env : {}),
  ...import.meta.env,
  ...((typeof window !== 'undefined' && window.env) || {}),
};

const env = envSchema.parse(envSource);

export default {
  ...env,
  isProd: env.NEXT_PUBLIC_TRASHLAB_ENV === 'production',
  isLocal: env.NEXT_PUBLIC_TRASHLAB_ENV === 'local',
  isStaging: env.NEXT_PUBLIC_TRASHLAB_ENV === 'staging',
  isTest: env.NEXT_PUBLIC_TRASHLAB_ENV === 'test',
  DEFAULT_TENANT_ID: env.NEXT_PUBLIC_DEFAULT_TENANT_ID,
  DEFAULT_DOMAIN: env.NEXT_PUBLIC_DEFAULT_DOMAIN,
};
